if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.submitButtonProductDisabled = this.submitButton.hasAttribute('disabled');
        this.requiresCustomName = this.dataset.requiresCustomName === 'true';
        this.customNameInput = this.querySelector('[data-custom-name-input]');
        this.customDateInput = this.querySelector('[data-custom-date-input]');
        this.customNameError = this.querySelector('[data-custom-name-error]');
        this.customNameErrorText = this.customNameError?.querySelector('.error-message');
        this.customDateError = this.querySelector('[data-custom-date-error]');
        this.customDateErrorText = this.customDateError?.querySelector('.error-message');
        this.dynamicCheckout = this.querySelector('[data-custom-name-dynamic-checkout]');
        this.customNameRequiredMessage = this.dataset.customNameRequiredMessage || '';
        this.customDateRequiredMessage = this.dataset.customDateRequiredMessage || '';
        this.customNameMaxLength = parseInt(this.customNameInput?.getAttribute('maxlength'), 10) || 30;
        this.customNameValid = !this.requiresCustomName;

        if (this.requiresCustomName && this.customNameInput) {
          this.customNameInput.addEventListener('input', this.onCustomNameInput.bind(this));
          this.customNameInput.addEventListener('blur', this.onCustomNameBlur.bind(this));
          this.updateCustomNameState({ trim: true });
        }

        if (this.requiresCustomName && this.customDateInput) {
          this.customDateInput.addEventListener('input', this.onCustomDateInput.bind(this));
          this.customDateInput.addEventListener('blur', this.onCustomDateBlur.bind(this));
          this.updateCustomDateState();
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.handleErrorMessage();
        if (this.requiresCustomName && !this.updateCustomNameState({ trim: true, showError: true })) {
          this.customNameInput?.focus();
          return;
        }
        if (this.requiresCustomName && !this.updateCustomDateState({ showError: true })) {
          this.customDateInput?.focus();
          return;
        }
        if (this.submitButton.hasAttribute('disabled') || this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.updateCustomDateFormData(formData);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      onCustomNameInput() {
        this.updateCustomNameState();
      }

      onCustomNameBlur() {
        this.updateCustomNameState({ trim: true, showError: true });
      }

      onCustomDateInput() {
        this.updateCustomDateState();
      }

      onCustomDateBlur() {
        this.updateCustomDateState({ showError: true });
      }

      sanitizeCustomNameValue(value) {
        if (typeof value !== 'string') return '';

        return value.replace(/[^A-Za-z0-9 '\-]/g, '').slice(0, this.customNameMaxLength);
      }

      formatCustomDateValue(value) {
        if (typeof value !== 'string') return '';

        const trimmedValue = value.trim();
        if (!trimmedValue) return '';

        const matchedDateParts = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!matchedDateParts) return trimmedValue;

        const [, year, month] = matchedDateParts;
        const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        const monthIndex = Number(month) - 1;

        if (monthIndex < 0 || monthIndex >= monthNames.length) return trimmedValue;

        return `${monthNames[monthIndex]}-${year}`;
      }

      updateCustomDateFormData(formData) {
        if (!formData || !this.customDateInput) return;

        const formattedDate = this.formatCustomDateValue(this.customDateInput.value);

        if (formattedDate) {
          formData.set('properties[Date]', formattedDate);
        } else {
          formData.delete('properties[Date]');
        }
      }

      updateCustomDateState({ showError = false } = {}) {
        if (!this.requiresCustomName || !this.customDateInput) return true;

        const customDateValid = this.customDateInput.value.trim().length > 0;
        this.toggleCustomDateError(showError && !customDateValid ? this.customDateRequiredMessage : '');

        return customDateValid;
      }

      updateCustomNameState({ trim = false, showError = false } = {}) {
        if (!this.requiresCustomName || !this.customNameInput) return true;

        const sanitizedValue = this.sanitizeCustomNameValue(this.customNameInput.value);
        const normalizedValue = trim ? sanitizedValue.trim() : sanitizedValue;

        if (this.customNameInput.value !== normalizedValue) {
          this.customNameInput.value = normalizedValue;
        }

        this.customNameValid = normalizedValue.trim().length > 0 && normalizedValue.length <= this.customNameMaxLength;
        this.toggleCustomNameError(showError && !this.customNameValid ? this.customNameRequiredMessage : '');
        this.updateCustomNameControls();

        return this.customNameValid;
      }

      toggleCustomNameError(message = '') {
        if (!this.customNameError || !this.customNameInput) return;

        this.customNameError.classList.toggle('hidden', !message);
        this.customNameInput.toggleAttribute('aria-invalid', Boolean(message));

        if (this.customNameErrorText) {
          this.customNameErrorText.textContent = message;
        }
      }

      toggleCustomDateError(message = '') {
        if (!this.customDateError || !this.customDateInput) return;

        this.customDateError.classList.toggle('hidden', !message);
        this.customDateInput.toggleAttribute('aria-invalid', Boolean(message));

        if (this.customDateErrorText) {
          this.customDateErrorText.textContent = message;
        }
      }

      updateCustomNameControls() {
        const shouldDisableSubmit = this.submitButtonProductDisabled || (this.requiresCustomName && !this.customNameValid);

        if (shouldDisableSubmit) {
          this.submitButton.setAttribute('disabled', 'disabled');
        } else {
          this.submitButton.removeAttribute('disabled');
        }

        if (this.dynamicCheckout) {
          this.dynamicCheckout.toggleAttribute('hidden', !this.customNameValid);
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.submitButtonProductDisabled = disable;

        if (disable) {
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }

        this.updateCustomNameControls();
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
